<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  eligibility: {
    type: Object,
    default: () => ({
      canRequest: true,
      reason: null,
      canManageCalling: false,
    }),
    validator: value => typeof value?.canRequest === 'boolean',
  },
});

const emit = defineEmits(['cancel', 'confirm']);

const { t } = useI18n();
const parserRef = ref(null);
const submitted = ref(false);

const isBusy = computed(() => props.isSubmitting || submitted.value);
const canRequest = computed(() => props.eligibility.canRequest);
const isInteractionDisabled = computed(() => isBusy.value || !canRequest.value);
const guidanceMessage = computed(() => {
  if (canRequest.value) return '';

  switch (props.eligibility.reason) {
    case 'calling_disabled':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.GUIDANCE.CALLING_DISABLED');
    case 'feature_unavailable':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.GUIDANCE.PLAN_UNAVAILABLE');
    case 'not_authorized':
    default:
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.GUIDANCE.NOT_AUTHORIZED');
  }
});

const cancel = () => {
  if (isBusy.value) return;

  emit('cancel');
};

const requestConfirmation = () => {
  if (isInteractionDisabled.value) return;

  parserRef.value?.sendMessage();
};

const confirm = payload => {
  if (isInteractionDisabled.value) return;

  submitted.value = true;
  emit('confirm', payload.templateParams);
};

watch(
  () => props.template,
  () => {
    submitted.value = false;
  }
);

watch(
  () => props.isSubmitting,
  (isSubmitting, wasSubmitting) => {
    if (wasSubmitting && !isSubmitting) submitted.value = false;
  }
);
</script>

<template>
  <section class="flex flex-col gap-4 w-full">
    <div
      data-testid="call-permission-explanation"
      class="flex gap-3 p-3 rounded-lg bg-n-blue-3 text-n-slate-12"
    >
      <span
        class="flex justify-center items-center flex-shrink-0 w-8 h-8 rounded-full bg-n-blue-5 text-n-blue-11"
        aria-hidden="true"
      >
        <Icon icon="i-ph-phone" class="size-4" />
      </span>
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-medium">
          {{ t('WHATSAPP_TEMPLATES.CALL_PERMISSION.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.CALL_PERMISSION.EXPLANATION') }}
        </p>
      </div>
    </div>

    <div
      :inert="isInteractionDisabled || undefined"
      :aria-disabled="isInteractionDisabled"
      :class="{ 'pointer-events-none opacity-60': isInteractionDisabled }"
    >
      <WhatsAppTemplateParser
        ref="parserRef"
        :template="template"
        @send-message="confirm"
      />
    </div>

    <div
      v-if="guidanceMessage"
      data-testid="call-permission-guidance"
      class="flex gap-2 items-start p-3 rounded-lg bg-n-amber-3 text-n-amber-12"
      role="status"
    >
      <Icon
        icon="i-ph-warning-circle"
        class="flex-shrink-0 mt-0.5 size-4 text-n-amber-11"
        aria-hidden="true"
      />
      <p class="text-sm text-n-amber-11">
        {{ guidanceMessage }}
      </p>
    </div>

    <footer class="flex gap-2 justify-end">
      <Button
        data-testid="call-permission-cancel"
        faded
        slate
        type="button"
        :label="t('WHATSAPP_TEMPLATES.CALL_PERMISSION.CANCEL')"
        :disabled="isBusy"
        @click="cancel"
      />
      <Button
        data-testid="call-permission-confirm"
        type="button"
        :label="t('WHATSAPP_TEMPLATES.CALL_PERMISSION.CONFIRM')"
        :disabled="isInteractionDisabled"
        :is-loading="isBusy"
        @click="requestConfirmation"
      />
    </footer>
  </section>
</template>
